import React, { useEffect, useState } from "react";
import { firebase } from "../../firebase/config";
import "./AdminMessageBanner.css";

// A message from Paul to one specific signed-in member.
//
// Why this exists: when a member's email address bounces there is no way to
// reach them at all. Their members/<uid> doc is already read with a live
// onSnapshot listener elsewhere in the app, so writing `adminMessage` onto it
// shows up on their screen immediately — even mid-session.
//
// Dismissal is stored in localStorage rather than written back to Firestore,
// so this needs no Firestore rules change and cannot fail a permissions check.
const dismissKey = (uid, text) => {
  // Key on the message itself so a NEW message reappears after an old dismissal.
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return `bc-admin-msg-${uid}-${hash}`;
};

const AdminMessageBanner = () => {
  const [message, setMessage] = useState(null);
  const [uid, setUid] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let unsubscribeDoc;
    const unsubscribeAuth = firebase.auth().onAuthStateChanged((user) => {
      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = undefined;
      }
      if (!user) {
        setMessage(null);
        setUid(null);
        return;
      }
      setUid(user.uid);
      unsubscribeDoc = firebase
        .firestore()
        .collection("members")
        .doc(user.uid)
        .onSnapshot(
          (snapshot) => {
            const data = snapshot.exists ? snapshot.data() : null;
            const text = data && typeof data.adminMessage === "string" ? data.adminMessage.trim() : "";
            setMessage(text || null);
            if (text) {
              setDismissed(!!window.localStorage.getItem(dismissKey(user.uid, text)));
            }
          },
          // A member who cannot read their own doc simply sees no banner.
          () => setMessage(null)
        );
    });
    return () => {
      if (unsubscribeDoc) unsubscribeDoc();
      unsubscribeAuth();
    };
  }, []);

  if (!message || dismissed) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(dismissKey(uid, message), "1");
    } catch (e) {
      // Private browsing can refuse localStorage; hiding it for this session is enough.
    }
    setDismissed(true);
  };

  return (
    <div className="AdminMessageBanner" role="status">
      <div className="AdminMessageBanner__inner">
        <span className="AdminMessageBanner__text">{message}</span>
        <button type="button" className="AdminMessageBanner__close" onClick={dismiss} aria-label="Dismiss message">
          &times;
        </button>
      </div>
    </div>
  );
};

export default AdminMessageBanner;
